import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, supabaseAPI } from '../config/supabase';

const SupabaseContext = createContext();

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
};

export const SupabaseProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [authEvent, setAuthEvent] = useState(null);
  const [myAdmin, setMyAdmin] = useState(null);

  // Supabase 연결 상태 확인
  useEffect(() => {
    const checkConnection = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // 환경변수 검증
        const { validateSupabaseConfig } = await import('../config/supabaseConfig');
        if (!validateSupabaseConfig()) {
          setError('Supabase 환경변수가 설정되지 않았습니다. .env 파일을 확인하세요.');
          setIsConnected(false);
          return;
        }
        
        // 간단한 쿼리로 연결 테스트
        const { data, error } = await supabase
          .from('accounts')
          .select('count', { count: 'exact', head: true });
        
        if (error) {
          console.error('Supabase 연결 오류:', error);
          setError(error.message);
          setIsConnected(false);
        } else {
          console.log('Supabase 연결 성공');
          setIsConnected(true);
        }
      } catch (err) {
        console.error('Supabase 연결 실패:', err);
        setError(err.message);
        setIsConnected(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkConnection();
  }, []);

  // Auth 세션 관리
  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsAuthLoading(true);
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession || null);
        setUser(currentSession?.user || null);
      } finally {
        setIsAuthLoading(false);
      }
    };

    const { data: authListener } = supabase.auth.onAuthStateChange((event, newSession) => {
      setAuthEvent(event || null);
      setSession(newSession || null);
      setUser(newSession?.user || null);
    });

    initAuth();

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Load my admin row when user changes
  useEffect(() => {
    const loadMyAdmin = async () => {
      if (!user) {
        console.log('No user, setting myAdmin to null');
        setMyAdmin(null);
        return;
      }
      
      console.log('Loading myAdmin for user:', user.id, user.email);
      const { data, error: err } = await supabase
        .from('admin')
        .select('*')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      
      if (err) {
        console.error('Error loading myAdmin:', err);
        return;
      }
      
      console.log('Loaded myAdmin:', data);
      setMyAdmin(data || null);
    };
    loadMyAdmin();
  }, [user]);

  const signInWithPassword = async ({ email, password }) => {
    setError(null);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) setError(err.message);
    return { error: err };
  };

  const signUpWithPassword = async ({ email, password }) => {
    setError(null);
    const redirectTo = window.location.origin + '/login';
    const { error: err } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectTo }
    });
    if (err) setError(err.message);
    return { error: err };
  };

  const sendPasswordReset = async (email) => {
    setError(null);
    const redirectTo = window.location.origin + '/login';
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (err) setError(err.message);
    return { error: err };
  };

  const updatePassword = async (newPassword) => {
    setError(null);
    const { error: err } = await supabase.auth.updateUser({ password: newPassword });
    if (err) setError(err.message);
    return { error: err };
  };

  const signOut = async () => {
    setError(null);
    await supabase.auth.signOut();
  };

  // 계정 데이터 로드
  const loadAccounts = async (page = 1, limit = 15) => {
    try {
      setError(null);
      const result = await supabaseAPI.getAccounts(page, limit);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // 계정 검색
  const searchAccounts = async (searchTerm, page = 1, limit = 15) => {
    try {
      setError(null);
      const result = await supabaseAPI.searchAccounts(searchTerm, page, limit);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // 락업 데이터 로드
  const loadLockups = async (page = 1, limit = 15) => {
    try {
      setError(null);
      const result = await supabaseAPI.getLockups(page, limit);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // 대시보드 통계 로드
  const loadDashboardStats = async () => {
    try {
      setError(null);
      const result = await supabaseAPI.getDashboardStats();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // 액티비티 로그 로드
  const loadActivityLogs = async (limit = 5) => {
    try {
      setError(null);
      const result = await supabaseAPI.getActivityLogs(limit);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Foundation Wallet 잔고 로드
  const loadFoundationWallets = async () => {
    try {
      setError(null);
      const result = await supabaseAPI.getFoundationWallets();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // 관리자 권한 확인
  const checkAdminAccess = async (address) => {
    try {
      setError(null);
      const result = await supabaseAPI.checkAdminAccess(address);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // 실시간 구독 설정
  const subscribeToTable = (tableName, callback) => {
    const subscription = supabase
      .channel(`${tableName}_changes`)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: tableName },
        callback
      )
      .subscribe();

    return subscription;
  };

  // 구독 해제
  const unsubscribe = (subscription) => {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  };

  const value = {
    // 상태
    isConnected,
    isLoading,
    error,
    // Auth
    session,
    user,
    isAuthLoading,
    authEvent,
    myAdmin,
    signInWithPassword,
    signUpWithPassword,
    sendPasswordReset,
    updatePassword,
    signOut,
    
    // API 함수들
    loadAccounts,
    searchAccounts,
    loadLockups,
    loadDashboardStats,
    loadActivityLogs,
    loadFoundationWallets,
    checkAdminAccess,
    
    // 실시간 구독
    subscribeToTable,
    unsubscribe,
    
    // 직접 API 접근
    supabaseAPI,
    supabase
  };

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
};

export default SupabaseContext;
